import * as THREE from 'three';
import { TileInfo, TileCoordinate } from '../Types';
import { LOADING } from './constants';
import { UrlBuilder } from './UrlBuilder';
import { TextureCache } from './TextureCache';
import { SpatialIndex } from './SpatialIndex';
import { createCancellableOperation, CancellableOperation } from './utils';

/**
 * Load operation with cancellation support
 */
interface LoadOperation extends CancellableOperation {
  tileInfo: TileInfo;
  promise: Promise<THREE.Texture>;
  generation: number;
}

/**
 * Events emitted by TileLoadQueue
 */
export interface TileLoadQueueEvents {
  tileLoaded: (tileInfo: TileInfo, texture: THREE.Texture) => void;
  tileLoadFailed: (tileInfo: TileInfo, error: Error) => void;
  loadStarted: (tileInfo: TileInfo) => void;
  loadCancelled: (tileInfo: TileInfo) => void;
}

/**
 * Statistics for the load queue
 */
export interface LoadQueueStats {
  queueSize: number;
  activeLoads: number;
  completedLoads: number;
  failedLoads: number;
  cancelledLoads: number;
  currentGeneration: number;
}

/**
 * Manages prioritized, concurrent tile loading with cancellation support
 */
export class TileLoadQueue {
  private readonly textureLoader: THREE.TextureLoader;
  private readonly textureCache: TextureCache;
  private readonly spatialIndex: SpatialIndex;
  private readonly maxConcurrent: number;

  private readonly loadQueue: TileInfo[] = [];
  private readonly activeLoads = new Map<string, LoadOperation>();
  private readonly loadingSpatialAreas = new Map<string, number>(); // spatialKey -> highest level being loaded

  // NEW: Track queued tiles to avoid duplicates
  private readonly queuedKeys = new Set<string>();

  private currentGeneration = 0;
  private completedLoads = 0;
  private failedLoads = 0;
  private cancelledLoads = 0;

  private readonly eventHandlers = new Map<keyof TileLoadQueueEvents, Set<Function>>();

  constructor(
    textureCache: TextureCache,
    spatialIndex: SpatialIndex,
    maxConcurrent = LOADING.MAX_CONCURRENT_LOADS
  ) {
    this.textureLoader = new THREE.TextureLoader();
    this.textureCache = textureCache;
    this.spatialIndex = spatialIndex;
    this.maxConcurrent = maxConcurrent;
  }

  /**
   * Enqueue tiles for loading with priority ordering and rapid transition optimization
   */
  async enqueue(tileInfos: TileInfo[]): Promise<void> {
    const generation = this.currentGeneration;

    // Group tiles by spatial area and select only the highest level for each area
    const spatialTileGroups = new Map<string, TileInfo>();

    for (const tileInfo of tileInfos) {
      const spatialKey = UrlBuilder.generateSpatialKey(tileInfo);
      const existingTile = spatialTileGroups.get(spatialKey);

      // Only keep the highest level tile for each spatial area
      if (!existingTile || tileInfo.level > existingTile.level) {
        spatialTileGroups.set(spatialKey, tileInfo);
        
        // Cancel any lower-level loads for this spatial area during rapid transitions
        this.cancelLowerLevelLoadsForArea(spatialKey, tileInfo.level);
      }
    }

    // Filter out tiles that are already loaded, loading, queued, or have higher level tiles
    const tilesToLoad: TileInfo[] = [];

    for (const tileInfo of spatialTileGroups.values()) {
      const tileKey = UrlBuilder.generateTileKey(tileInfo);
      const spatialKey = UrlBuilder.generateSpatialKey(tileInfo);

      // Skip if already queued
      if (this.queuedKeys.has(tileKey)) {
        continue;
      }

      // Skip if already visible, loading, or higher level tile exists/loading
      if (
        this.spatialIndex.hasTile(tileKey) ||
        this.activeLoads.has(tileKey) ||
        this.spatialIndex.isHigherLevelTileLoaded(spatialKey, tileInfo.level)
      ) {
        continue;
      }

      // Mark this spatial area as loading at this level
      const currentLoadingLevel = this.loadingSpatialAreas.get(spatialKey);
      if (!currentLoadingLevel || tileInfo.level > currentLoadingLevel) {
        this.loadingSpatialAreas.set(spatialKey, tileInfo.level);
        this.spatialIndex.markSpatialAreaLoading(spatialKey, tileInfo.level);
      }

      tilesToLoad.push(tileInfo);
    }

    // Add to queue (will be sorted by priority) and mark keys as queued
    for (const ti of tilesToLoad) {
      this.loadQueue.push(ti);
      this.queuedKeys.add(UrlBuilder.generateTileKey(ti)); // NEW
    }
    this.loadQueue.sort((a, b) => b.priority - a.priority);

    // Start processing if there are available slots
    this.processQueue(generation);
  }

  /**
   * Process the load queue with concurrency limits
   */
  private async processQueue(generation: number): Promise<void> {
    while (
      this.loadQueue.length > 0 &&
      this.activeLoads.size < this.maxConcurrent &&
      generation === this.currentGeneration
    ) {
      const tileInfo = this.loadQueue.shift()!;
      // NEW: remove from queued tracking now that we're starting it
      this.queuedKeys.delete(UrlBuilder.generateTileKey(tileInfo));
      this.startLoad(tileInfo, generation);
    }
  }

  /**
   * Start loading a single tile
   */
  private startLoad(tileInfo: TileInfo, generation: number): void {
    const tileKey = UrlBuilder.generateTileKey(tileInfo);
    const spatialKey = UrlBuilder.generateSpatialKey(tileInfo);

    // Check cache first
    if (this.textureCache.has(tileInfo.url)) {
      const texture = this.textureCache.get(tileInfo.url)!;

      // Clear loading state
      this.clearLoadingState(spatialKey, tileInfo.level);

      // Emit success event
      this.emit('tileLoaded', tileInfo, texture);

      // Continue processing queue
      this.processQueue(generation);
      return;
    }

    // Create cancellable operation
    const operation = createCancellableOperation();

    // Create load promise
    const promise = this.loadTexture(tileInfo.url)
      .then((texture) => {
        // Check if operation was cancelled
        if (operation.isCancelled || generation !== this.currentGeneration) {
          this.cancelledLoads++;
          this.emit('loadCancelled', tileInfo);
         // throw new Error('Load operation was cancelled');
        }

        // Cache the texture
        this.textureCache.put(tileInfo.url, texture);

        this.completedLoads++;
        this.emit('tileLoaded', tileInfo, texture);

        return texture;
      })
      .catch((error) => {
        if (operation.isCancelled || generation !== this.currentGeneration) {
          this.cancelledLoads++;
          this.emit('loadCancelled', tileInfo);
        } else {
          this.failedLoads++;
          this.emit('tileLoadFailed', tileInfo, error);
        }
        throw error;
      })
      .finally(() => {
        // Clean up
        this.activeLoads.delete(tileKey);
        this.clearLoadingState(spatialKey, tileInfo.level);

        // Continue processing queue
        this.processQueue(generation);
      });

    // Track active load
    const loadOperation: LoadOperation = {
      tileInfo,
      promise,
      generation,
      cancel: operation.cancel,
      isCancelled: operation.isCancelled,
    };

    this.activeLoads.set(tileKey, loadOperation);
    this.emit('loadStarted', tileInfo);
  }

  /**
   * Load texture using THREE.TextureLoader
   */
  private loadTexture(url: string): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      this.textureLoader.load(
        url,
        (texture) => {
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;
          texture.generateMipmaps = false;

          // Tag texture with URL for ref counting
          (texture as any).__tileUrl = url;

          resolve(texture);
        },
        undefined,
        (error) => {
          reject(error);
        }
      );
    });
  }

  /**
   * Clear loading state for spatial area
   */
  private clearLoadingState(spatialKey: string, level: number): void {
    const currentLoadingLevel = this.loadingSpatialAreas.get(spatialKey);
    if (currentLoadingLevel === level) {
      this.loadingSpatialAreas.delete(spatialKey);
      this.spatialIndex.clearSpatialAreaLoading(spatialKey, level);
    }
  }

  /**
   * Cancel lower-level loads for a specific spatial area when higher level is requested
   */
  private cancelLowerLevelLoadsForArea(spatialKey: string, newLevel: number): void {
    // Cancel active loads for lower levels in this spatial area
    const loadsToCancel: string[] = [];
    
    for (const [tileKey, operation] of this.activeLoads) {
      const tileInfo = operation.tileInfo;
      const tileSpatialKey = UrlBuilder.generateSpatialKey(tileInfo);
      
      if (tileSpatialKey === spatialKey && tileInfo.level < newLevel) {
        operation.cancel();
        loadsToCancel.push(tileKey);
      }
    }
    
    // Remove cancelled loads from active loads
    for (const tileKey of loadsToCancel) {
      this.activeLoads.delete(tileKey);
    }
    
    // Remove lower-level tiles from queue for this spatial area
    const originalQueueLength = this.loadQueue.length;
    const tilesToRemove: number[] = [];
    
    for (let i = this.loadQueue.length - 1; i >= 0; i--) {
      const tileInfo = this.loadQueue[i];
      const tileSpatialKey = UrlBuilder.generateSpatialKey(tileInfo);
      
      if (tileSpatialKey === spatialKey && tileInfo.level < newLevel) {
        this.queuedKeys.delete(UrlBuilder.generateTileKey(tileInfo));
        tilesToRemove.push(i);
      }
    }
    
    // Remove tiles in reverse order to maintain indices
    for (const index of tilesToRemove) {
      this.loadQueue.splice(index, 1);
    }
    
    // Lower-level loads cancelled for spatial area
  }

  /**
   * Cancel all pending and active loads (increment generation)
   */
  cancelAllLoads(): void {
     this.currentGeneration++;
 
     for (const [tileKey, operation] of this.activeLoads.entries()) {
       operation.cancel();
     }
 
     // Clear active loads immediately
     this.activeLoads.clear();
 
     // Clear queued tiles & loading state
     this.loadQueue.length = 0;
     this.queuedKeys.clear();
    for (const [spatialKey, level] of this.loadingSpatialAreas.entries()) {
      this.spatialIndex.clearSpatialAreaLoading(spatialKey, level);
    }
    this.loadingSpatialAreas.clear();
  }

  /**
   * Cancel loads for specific spatial areas
   */
  cancelLoadsForSpatialAreas(spatialKeys: string[]): void {
    const spatialKeySet = new Set(spatialKeys);

    // Cancel active loads
    for (const [tileKey, operation] of this.activeLoads.entries()) {
      const spatialKey = UrlBuilder.generateSpatialKey(operation.tileInfo);
      if (spatialKeySet.has(spatialKey)) {
        operation.cancel();
      }
    }

    // Remove from queue (and queuedKeys)
    for (let i = this.loadQueue.length - 1; i >= 0; i--) {
      const tileInfo = this.loadQueue[i];
      const spatialKey = UrlBuilder.generateSpatialKey(tileInfo);
      if (spatialKeySet.has(spatialKey)) {
        const key = UrlBuilder.generateTileKey(tileInfo);
        this.queuedKeys.delete(key); // NEW
        this.loadQueue.splice(i, 1);
      }
    }

    // Clear loading state map entries (local only)
    for (const spatialKey of spatialKeys) {
      this.loadingSpatialAreas.delete(spatialKey);
    }
  }

  /**
   * Get current load queue statistics
   */
  getStats(): LoadQueueStats {
    return {
      queueSize: this.loadQueue.length,
      activeLoads: this.activeLoads.size,
      completedLoads: this.completedLoads,
      failedLoads: this.failedLoads,
      cancelledLoads: this.cancelledLoads,
      currentGeneration: this.currentGeneration,
    };
  }

  /**
   * Get tiles currently being loaded
   */
  getActiveLoads(): TileInfo[] {
    return Array.from(this.activeLoads.values()).map((op) => op.tileInfo);
  }

  /**
   * Get tiles in queue waiting to be loaded
   */
  getQueuedTiles(): TileInfo[] {
    return [...this.loadQueue];
  }

  /**
   * Check if a tile is currently loading
   */
  isLoading(tileKey: string): boolean {
    return this.activeLoads.has(tileKey);
  }

  /**
   * Check if a tile is in the queue
   */
  isQueued(tileInfo: TileInfo): boolean {
    const tileKey = UrlBuilder.generateTileKey(tileInfo);
    return this.queuedKeys.has(tileKey); // NEW (O(1))
  }

  /**
   * Wait for all active loads to complete
   */
  async waitForCompletion(): Promise<void> {
    const activePromises = Array.from(this.activeLoads.values()).map((op) =>
      op.promise.catch(() => {}) // Ignore errors, just wait for completion
    );

    await Promise.all(activePromises);
  }

  /**
   * Clear all statistics
   */
  resetStats(): void {
    this.completedLoads = 0;
    this.failedLoads = 0;
    this.cancelledLoads = 0;
  }

  /**
   * Add event listener
   */
  on<K extends keyof TileLoadQueueEvents>(event: K, handler: TileLoadQueueEvents[K]): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  /**
   * Remove event listener
   */
  off<K extends keyof TileLoadQueueEvents>(event: K, handler: TileLoadQueueEvents[K]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Emit event
   */
  private emit<K extends keyof TileLoadQueueEvents>(
    event: K,
    ...args: Parameters<TileLoadQueueEvents[K]>
  ): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      for (const handler of handlers) {
        try {
          (handler as any)(...args);
        } catch (error) {
          // Silently handle event errors
        }
      }
    }
  }

  /**
   * Dispose all resources
   */
  dispose(): void {
    this.cancelAllLoads();
    this.eventHandlers.clear();
    this.resetStats();
  }
}
